import React from 'react';
import { ActivityIndicator, Pressable, TextInput, View } from 'react-native';
import { Text } from '@constants/Themed';
import { getPillButtonStyle } from '@stylesheet';
import { responsiveFontSize } from '@utils/responsiveFontSize';

export type PromoCodeFieldStatus = { tone: 'success' | 'error' | 'muted'; text: string } | null;

type PromoCodeFieldProps = {
  theme: boolean;
  label?: string;
  value: string;
  onChangeText: (value: string) => void;
  onSubmit: () => void;
  placeholder: string;
  buttonLabel: string;
  loading?: boolean;
  disabled?: boolean;
  status?: PromoCodeFieldStatus;
};

const STATUS_COLORS = {
  success: { light: '#047857', dark: '#6ee7b7' },
  error: { light: '#b91c1c', dark: '#fca5a5' },
  muted: { light: '#64748b', dark: '#94a3b8' },
} as const;

export const PromoCodeField: React.FC<PromoCodeFieldProps> = ({
  theme,
  label,
  value,
  onChangeText,
  onSubmit,
  placeholder,
  buttonLabel,
  loading = false,
  disabled = false,
  status = null,
}) => {
  const canSubmit = !disabled && !loading && value.trim().length > 0;

  return (
    <View style={{ width: '100%', gap: 6 }}>
      {label ? (
        <Text
          style={{
            fontSize: responsiveFontSize(13),
            fontWeight: '600',
            color: theme ? '#334155' : '#cbd5e1',
          }}
        >
          {label}
        </Text>
      ) : null}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <TextInput
          value={value}
          onChangeText={(text) => onChangeText(text.toUpperCase())}
          onSubmitEditing={() => canSubmit && onSubmit()}
          placeholder={placeholder}
          placeholderTextColor={theme ? '#94a3b8' : '#64748b'}
          autoCapitalize="characters"
          autoCorrect={false}
          editable={!disabled && !loading}
          returnKeyType="done"
          style={{
            flex: 1,
            minWidth: 0,
            height: 40,
            paddingHorizontal: 14,
            borderRadius: 9999,
            borderWidth: 1,
            borderColor: theme ? '#cbd5e1' : '#475569',
            backgroundColor: theme ? '#ffffff' : 'rgba(15, 23, 42, 0.6)',
            color: theme ? '#0f172a' : '#f1f5f9',
            fontSize: responsiveFontSize(14),
            letterSpacing: 1,
          }}
        />
        <Pressable
          onPress={onSubmit}
          disabled={!canSubmit}
          accessibilityRole="button"
          accessibilityLabel={buttonLabel}
          style={[getPillButtonStyle(theme, 'primary'), { minWidth: 84, opacity: canSubmit ? 1 : 0.5 }]}
        >
          {loading ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <Text style={{ color: '#fff', fontSize: responsiveFontSize(13), fontWeight: '500' }}>
              {buttonLabel}
            </Text>
          )}
        </Pressable>
      </View>
      {status?.text ? (
        <Text
          style={{
            fontSize: responsiveFontSize(12),
            fontWeight: '600',
            color: STATUS_COLORS[status.tone][theme ? 'light' : 'dark'],
          }}
        >
          {status.text}
        </Text>
      ) : null}
    </View>
  );
};

export default PromoCodeField;
